import type * as React from "react";

/**
 * The style-prop vocabulary shared by every primitive in this directory.
 *
 * These types are internal to `src/ui`. `ResponsiveValue` is also re-exported from the public barrel
 * so a consumer prop type can name it; no file outside `src/ui` does today. Nothing here references
 * a Mantine type — the `lint:ui-types` gate fails the build if one crosses an exported signature.
 */

export type Size = number | "xs" | "sm" | "md" | "lg" | "xl";

/**
 * `Size`, plus any other string. The app's theme extends Mantine's size scale (`xxs` is in live
 * use) and the call sites also pass raw CSS lengths such as `"35px"` and `"1rem"`. Intersecting
 * with `Record<never, never>` stops the union collapsing to plain `string`, so the named scale
 * still appears in editor autocomplete. (`string & {}` is the usual spelling; `ban-types` rejects
 * the bare `{}` and this is the same type.)
 */
export type SizeValue = Size | (string & Record<never, never>);

/** A theme color key or a plain CSS color value. */
export type ThemeColor = string;

/**
 * Converts pixel values to rems with Mantine 6's existing 16px base conversion. The supported
 * public input matches the call sites: numbers and CSS strings. Non-pixel strings are preserved.
 */
export const rem = (value: number | string): string => {
  if (typeof value === "number") {
    return `${value / 16}rem`;
  }

  const numericValue = value.replace("px", "");
  return Number.isNaN(Number(numericValue)) ? value : `${Number(numericValue) / 16}rem`;
};

type Breakpoint = "base" | "xs" | "sm" | "md" | "lg" | "xl";

export type ResponsiveValue<T> = T | Partial<Record<Breakpoint, T>>;

export type ResponsiveCssProperty<Property extends keyof React.CSSProperties> = ResponsiveValue<React.CSSProperties[Property]>;

export interface StyleProps {
  m?: ResponsiveCssProperty<"margin">;
  my?: ResponsiveCssProperty<"margin">;
  mx?: ResponsiveCssProperty<"margin">;
  mt?: ResponsiveCssProperty<"marginTop">;
  mb?: ResponsiveCssProperty<"marginBottom">;
  ml?: ResponsiveCssProperty<"marginLeft">;
  mr?: ResponsiveCssProperty<"marginRight">;
  p?: ResponsiveCssProperty<"padding">;
  py?: ResponsiveCssProperty<"padding">;
  px?: ResponsiveCssProperty<"padding">;
  pt?: ResponsiveCssProperty<"paddingTop">;
  pb?: ResponsiveCssProperty<"paddingBottom">;
  pl?: ResponsiveCssProperty<"paddingLeft">;
  pr?: ResponsiveCssProperty<"paddingRight">;
  bg?: ResponsiveCssProperty<"backgroundColor">;
  c?: ResponsiveCssProperty<"color">;
  opacity?: ResponsiveCssProperty<"opacity">;
  ff?: ResponsiveCssProperty<"fontFamily">;
  fz?: ResponsiveCssProperty<"fontSize">;
  fw?: ResponsiveCssProperty<"fontWeight">;
  lts?: ResponsiveCssProperty<"letterSpacing">;
  ta?: ResponsiveCssProperty<"textAlign">;
  lh?: ResponsiveCssProperty<"lineHeight">;
  fs?: ResponsiveCssProperty<"fontStyle">;
  tt?: ResponsiveCssProperty<"textTransform">;
  td?: ResponsiveCssProperty<"textDecoration">;
  w?: ResponsiveCssProperty<"width">;
  miw?: ResponsiveCssProperty<"minWidth">;
  maw?: ResponsiveCssProperty<"maxWidth">;
  h?: ResponsiveCssProperty<"height">;
  mih?: ResponsiveCssProperty<"minHeight">;
  mah?: ResponsiveCssProperty<"maxHeight">;
  bgsz?: ResponsiveCssProperty<"backgroundSize">;
  bgp?: ResponsiveCssProperty<"backgroundPosition">;
  bgr?: ResponsiveCssProperty<"backgroundRepeat">;
  bga?: ResponsiveCssProperty<"backgroundAttachment">;
  pos?: ResponsiveCssProperty<"position">;
  top?: ResponsiveCssProperty<"top">;
  left?: ResponsiveCssProperty<"left">;
  bottom?: ResponsiveCssProperty<"bottom">;
  right?: ResponsiveCssProperty<"right">;
  inset?: ResponsiveCssProperty<"inset">;
  display?: ResponsiveCssProperty<"display">;
}

/**
 * The intrinsic-element props a primitive forwards, minus every name the style-prop vocabulary
 * already owns. Without the `Omit`, `color` and `size` would collide with their HTML namesakes.
 */
export type ElementProps<Element extends keyof React.JSX.IntrinsicElements> = Omit<
  React.ComponentPropsWithoutRef<Element>,
  keyof StyleProps
>;
